import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    leaderboardFavouriteSelector,
    leaderboardFilterSelector,
    leaderboardNavSelector,
    leaderboardDataSelector
} from 'common/store/modules/brownlow-tracker/leaderboard/selectors';

import {
    useGetBrownlowPredictorQuery,
    useGetBrownlowPredictorSearchQuery,
    useGetBrownlowPredictorFavouritesQuery,
    useGetTeamsQuery
} from 'common/store/modules/api/afl-api';

import { useBettingVis } from 'common/react/hooks/use-betting-vis';
import { useGetBrownlowBettingOddsQuery } from 'common/store/modules/api/cfs-api';

import { usePagination } from 'widgets/player-awards-tracker/js/components/hooks/use-fetch-more-data';
import { useCurrentPlayerPulseIds } from 'widgets/player-awards-tracker/js/components/Leaderboard/hooks/use-current-player-pulse-ids';

import {
    assignPositions,
    composeColumnData,
    composePredictorRowData
} from 'widgets/player-awards-tracker/js/components/Leaderboard/components/table/utils';

import {
    UPDATE_FAVOURITE_PLAYER,
    FETCH_LEADERBOARD_DATA
} from 'common/store/modules/brownlow-tracker/leaderboard/actions';
import {
    sortFavourites,
    sortPlayersByTotalVotesByNameByFirstName
} from '../utils/utils';

const PAGE_SIZE = 20;

/**
 *
 * @param playersData
 * @param teams
 * @param shouldShowBettingOdds
 * @param bettingOddsData
 */
function mapPlayersForTable(
    playersData,
    teams,
    shouldShowBettingOdds,
    bettingOddsData
) {
    return (
        playersData?.results?.map((item) => {
            const roundByRoundVotes = Object.keys(item.rounds).map((key) => ({
                matchId: '',
                roundNumber: key,
                votes: item.rounds[key]?.points ?? ''
            }));

            const teamObject = teams.find(
                (teamItem) => teamItem.id === item.teamId
            );

            return {
                eligible: item.eligible ?? false,
                player: { ...item },
                roundByRoundVotes,
                team: {
                    teamAbbr: teamObject?.abbreviation,
                    teamId: teamObject?.providerId,
                    teamName: teamObject?.name,
                    teamNickname: teamObject?.nickname
                },
                totalVotes: item.totalVotes ?? '',
                bettingOdds: shouldShowBettingOdds
                    ? bettingOddsData?.outcomes?.find(
                          (outcome) => item?.providerId === outcome?.playerId
                      )?.price ?? null
                    : null
            };
        }) ?? []
    );
}

/**
 *
 * @param seasonId
 */
export function useBrownlowPredictorData(seasonId) {
    const dispatch = useDispatch();

    const favourites = useSelector(leaderboardFavouriteSelector);
    const leaderboardFilter = useSelector(leaderboardFilterSelector);
    const nav = useSelector(leaderboardNavSelector);

    const seasons = useSelector(
        (state) =>
            state.seasons[`competition_${nav.competition.id}`]?.list ?? []
    );
    const season = seasons.find((item) => item.id === nav.season.id);
    const seasonPrId = season?.providerId ?? '';

    const currentRoundNumber = season?.currentRoundNumber;

    const rounds = useSelector(
        (state) => state.rounds[`season_${nav.season.id}`]?.list ?? []
    );

    const { data: teamsData } = useGetTeamsQuery(nav.season.id, {
        skip: !nav.season.id
    });

    // Avoid re-creating primitive values unless they actually change
    const teamsList = teamsData || [];
    const selectedTeam = teamsList.find((item) => item.id === nav.team.id);
    const teams = teamsList;
    const teamId = selectedTeam?.providerId ?? '';
    const teamFilter = nav.team.id;

    const [data, setData] = useState([]);
    const [noDataAvailable, setNoDataAvailable] = useState(false);
    const [sortByFavourites, setSortByFavourites] = useState(false);
    const [hideLoadMoreBtn, setHideLoadMoreBtn] = useState(false);
    const [currentFavouriteIds, setCurrentFavouriteIds] = useState([]);

    const [
        shownData,
        fetchMoreData,
        currentPage,
        setShownData,
        setCurrentPage
    ] = usePagination(data, PAGE_SIZE, teamId);

    const { data: bettingOddsData } = useGetBrownlowBettingOddsQuery(null, {
        pollingInterval: PULSE.app.common.CONSTANTS.TIME.ONE_MINUTE_IN_MS
    });

    const shouldShowBettingOdds =
        useBettingVis() &&
        bettingOddsData?.displayInApp === true &&
        bettingOddsData?.outcomes?.length > 0 &&
        season?.id === Number(seasonId);

    const teamIdFromFilters = teamFilter !== -1 ? teamFilter.toString() : '';

    const [combinedPlayers, setCombinedPlayers] = useState({
        results: [],
        newItems: []
    });

    const lastProcessedPlayers = useRef(null);

    const {
        data: players,
        isLoading,
        isFetching,
        isError: isPredictorError
    } = useGetBrownlowPredictorQuery(
        {
            compSeasonId: season ? season.id : null,
            page: currentPage - 1,
            team: teamIdFromFilters
        },
        {
            skip: !(
                season?.id &&
                (leaderboardFilter.query === null ||
                    leaderboardFilter.query === '')
            )
        },
        { refetchOnMountOrArgChange: true }
    );

    useEffect(() => {
        if (players && players !== lastProcessedPlayers.current) {
            setCombinedPlayers((current) => {
                return {
                    results: [...current.results, ...players.results],
                    newItems: players.newItems
                };
            });

            lastProcessedPlayers.current = players;
        }
    }, [players]);

    // Favourite players rows (for pulse IDs)
    const favouriteRows = useMemo(() => {
        const rows = favourites.map((player) => ({
            player: {
                ...player,
                playerId: player.providerId
            }
        }));
        return { rows, pageSize: rows.length };
    }, [favourites, sortByFavourites]);

    useCurrentPlayerPulseIds({
        rowData: favouriteRows.rows,
        currentPage,
        pageSize: favouriteRows.pageSize,
        currentPlayerIds: currentFavouriteIds,
        setCurrentPlayerIds: setCurrentFavouriteIds
    });

    const { data: favouritedPlayers = [] } =
        useGetBrownlowPredictorFavouritesQuery(
            {
                compSeasonId: season ? season.id : null,
                players: currentFavouriteIds
            },
            {
                skip:
                    !(
                        season?.id &&
                        (leaderboardFilter.query === null ||
                            leaderboardFilter.query === '')
                    ) ||
                    currentFavouriteIds.length === 0 ||
                    !sortByFavourites
            },
            { refetchOnMountOrArgChange: true }
        );

    const {
        data: searchPlayers,
        isLoading: isLoadingSearch,
        isFetching: isFetchingSearch,
        isError: isPlayerSearchError
    } = useGetBrownlowPredictorSearchQuery(
        {
            compSeasonId: season ? season.id : null,
            term: leaderboardFilter.query
        },
        {
            skip:
                leaderboardFilter.query === null ||
                leaderboardFilter.query === ''
        }
    );

    const onPlayerFavouriteClick = (playerId) => {
        dispatch(
            UPDATE_FAVOURITE_PLAYER.request({
                id: null,
                providerId: playerId
            })
        );
    };

    // Effect to set row data
    useEffect(() => {
        setNoDataAvailable(false);

        if (
            !combinedPlayers ||
            (!combinedPlayers?.results?.length &&
                !searchPlayers?.results?.length) ||
            isPlayerSearchError ||
            isPredictorError
        ) {
            if (!isPlayerSearchError) {
                setNoDataAvailable(true);
            }
            setShownData([]);
            return;
        }

        let searchPlayersSorted;
        if (leaderboardFilter.query && leaderboardFilter.query !== '') {
            if (searchPlayers && searchPlayers.results?.length) {
                searchPlayersSorted = {
                    results: sortPlayersByTotalVotesByNameByFirstName(
                        searchPlayers.results
                    )
                };
                if (searchPlayers.newItems?.length) {
                    searchPlayersSorted.newItems = searchPlayers.newItems;
                }
            } else {
                setShownData([]);
                return;
            }
        }

        const playersData =
            leaderboardFilter.query && searchPlayersSorted
                ? searchPlayersSorted
                : combinedPlayers;

        setHideLoadMoreBtn(playersData?.newItems?.length <= PAGE_SIZE);

        let items = mapPlayersForTable(
            playersData,
            teams,
            shouldShowBettingOdds,
            bettingOddsData
        );

        if (favourites.length && sortByFavourites) {
            items = sortFavourites(items, favourites, teamId);
        }

        setData(assignPositions(items));
    }, [
        favourites,
        sortByFavourites,
        teamId,
        bettingOddsData,
        shouldShowBettingOdds,
        seasonPrId,
        teamsData,
        combinedPlayers,
        searchPlayers,
        isPlayerSearchError,
        isPredictorError,
        leaderboardFilter
    ]);

    // Map favourites to merge with table data
    const favouritesToMerge = useMemo(() => {
        if (!favouritedPlayers?.length) {
            return [];
        }
        const favouritePlayersWrapped = { results: favouritedPlayers || [] };
        return mapPlayersForTable(
            favouritePlayersWrapped,
            teams,
            shouldShowBettingOdds,
            bettingOddsData
        );
    }, [favouritedPlayers, teams, shouldShowBettingOdds, bettingOddsData]);

    // Merge table data and favourites
    useEffect(() => {
        if (
            leaderboardFilter.query &&
            leaderboardFilter.query !== '' &&
            searchPlayers?.results?.length === shownData?.length
        ) {
            setHideLoadMoreBtn(true);
            return;
        }

        const merged = [
            ...data.slice(0, PAGE_SIZE * currentPage),
            ...(sortByFavourites ? favouritesToMerge : [])
        ];
        const mergedTableAndFavs = Array.from(
            new Map(
                merged.map((item) => [item.player.providerId, item])
            ).values()
        );

        if (
            shownData.length !== mergedTableAndFavs.length ||
            !shownData.every(
                (item, i) =>
                    item.player.providerId ===
                    mergedTableAndFavs[i].player.providerId
            )
        ) {
            setShownData(mergedTableAndFavs);
        }
    }, [
        data,
        currentPage,
        favouritesToMerge,
        leaderboardFilter.query,
        searchPlayers?.results?.length,
        sortByFavourites
    ]);
    // Fetch leaderboard data
    useEffect(() => {
        dispatch(
            FETCH_LEADERBOARD_DATA.request({
                type: 'season',
                seasonPid: seasonPrId,
                teamId: teamId ?? ''
            })
        );
    }, [seasonPrId, teamId]);

    // Columns
    const columns = useMemo(() => composeColumnData(rounds), [rounds]);

    // Row data
    const rowData = useMemo(() => {
        const predictedFromShownData = shownData.map(
            (dataObject) => dataObject.player
        );
        return shownData.map((item) => {
            const yearFromCompSeason = seasonPrId.replace('CD_S', '');
            const playerFromProviderId = item.player.providerId.replace(
                'CD_I',
                ''
            );
            let headshotUrl;

            if (yearFromCompSeason) {
                headshotUrl = `https:${
                    PULSE.app.environment.playerImagePath
                }/${'AFL'}/${yearFromCompSeason}/${playerFromProviderId}.png?im=Scale,width=0.6,height=0.6`;
            }

            item.player.photoURL = headshotUrl;

            return composePredictorRowData(
                item,
                columns.filter(
                    (col) =>
                        !['player', 'totalVotes', 'bettingOdds'].includes(
                            col.accessor
                        )
                ),
                rounds,
                leaderboardFilter.query && leaderboardFilter.query !== ''
                    ? searchPlayers?.results
                    : predictedFromShownData
            );
        });
    }, [
        columns,
        shownData,
        rounds,
        seasonPrId,
        searchPlayers,
        leaderboardFilter
    ]);

    // Leaderboard data
    const leaderboardData = useSelector((state) =>
        leaderboardDataSelector(state, seasonPrId)
    );

    // Reset current page on team change
    useEffect(() => {
        setCurrentPage((prev) => (prev !== 1 ? 1 : prev));
    }, [teamId, setCurrentPage]);

    return {
        isLoading,
        isLoadingSearch,
        noDataAvailable,
        rowData,
        columns,
        favourites,
        sortByFavourites,
        setSortByFavourites,
        onPlayerFavouriteClick,
        isFetching: isFetching || isFetchingSearch,
        hideLoadMoreBtn,
        fetchMoreData,
        seasonPrId,
        shouldShowBettingOdds,
        leaderboardFilter,
        leaderboardData,
        currentRoundNumber
    };
}
